import { useState, type ReactNode } from 'react';
import { Button, Text, TextField } from '@backstage/ui';
import { useApi, useTranslationRef } from '@backstage/frontend-plugin-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import type {
  FieldValues,
  Reservation,
} from '@internal/plugin-waadoo-config-common';
import { SidePanel, SidePanelSection } from '../../waadooForm';
import { formatCpu, formatMemory } from '../../waadooForm/formats';
import { EnvironmentTag } from '../BranchLabel';
import {
  configureNeedsConfirmation,
  defaultReference,
  launchConfigure,
  referenceOptions,
  useReferenceConfiguration,
  type ConfigurationReference,
  type ConfigurationResponse,
} from '../configuration';
import { confirmationText } from '../environmentPower';
import { projectEnvironmentsTranslationRef } from '../translation';
import { signed, useClusterMargin } from './clusterMargin';
import { QuotaGauges } from './QuotaGauges';
import type { ConfigurationChoice } from './useConfigurationRows';

/** The id of « aucun » among the references: never an environment's name. */
const NO_REFERENCE = '#none';

/** « Comparer avec » of a tab: the reference offered, picked and read. */
export interface ConfigurationReferenceState<C extends FieldValues> {
  /** What « Comparer avec » offers (`referenceOptions`). */
  references: ConfigurationReference[];
  /** The environment compared with; undefined for « aucun ». */
  referenceEnvironment: string | undefined;
  /** What the tab calls it (`dev-*` for the template). */
  referenceLabel: string | undefined;
  /** Its configuration, once read. */
  reference: C | undefined;
  /** Picks a reference by its id among `references`, or « aucun ». */
  select(id: string): void;
}

/**
 * The reference of a tab's « Comparer avec »: the default one
 * (`defaultReference`), which follows the permanent environments as they are
 * read, until the reader picks one.
 */
export function useConfigurationReference<C extends FieldValues>(input: {
  project: string;
  branch: string;
  permanentNames: string[];
  reloadSignal?: number;
}): ConfigurationReferenceState<C> {
  const { project, branch, permanentNames } = input;
  // Undefined: the default reference; set once the reader picks one.
  const [choice, setChoice] = useState<string>();
  const references = referenceOptions(branch, permanentNames);
  const referenceEnvironment =
    choice === NO_REFERENCE
      ? undefined
      : choice ?? defaultReference(branch, permanentNames);
  const reference = useReferenceConfiguration<C>(
    project,
    referenceEnvironment,
    input.reloadSignal,
  );
  const referenceOption = references.find(
    option => option.environment === referenceEnvironment,
  );
  return {
    references,
    referenceEnvironment,
    referenceLabel: referenceOption?.label ?? referenceEnvironment,
    reference,
    select: setChoice,
  };
}

/** « Appliquer » of a tab: the prod confirmation, the launch, its failure. */
export interface ConfigurationApply {
  /** Whether the branch is prod: `<project>/<branch>` typed first. */
  production: boolean;
  /** What to type (`confirmationText`). */
  expected: string;
  typed: string;
  setTyped(value: string): void;
  /** The launch running: every input is disabled. */
  pending: boolean;
  failure: string | undefined;
  /** Whether « Appliquer » may be pressed. */
  canApply(input: {
    editable: boolean;
    changes: number;
    problems: number;
  }): boolean;
  /** Launches `env-configure` with `fields`; the page leads to the task. */
  apply(fields: Partial<FieldValues>): Promise<void>;
}

export function useConfigurationApply(input: {
  project: string;
  branch: string;
  templateRef: string | undefined;
  onLaunched: (taskId: string) => void;
}): ConfigurationApply {
  const { project, branch, templateRef, onLaunched } = input;
  const scaffolderApi = useApi(scaffolderApiRef);
  const [typed, setTyped] = useState('');
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string>();
  const production = configureNeedsConfirmation(branch);
  const expected = confirmationText(project, branch);
  return {
    production,
    expected,
    typed,
    setTyped,
    pending,
    failure,
    canApply: ({ editable, changes, problems }) =>
      templateRef !== undefined &&
      editable &&
      !pending &&
      changes > 0 &&
      problems === 0 &&
      (!production || typed === expected),
    apply: async fields => {
      if (templateRef === undefined) return;
      setPending(true);
      setFailure(undefined);
      try {
        onLaunched(
          await launchConfigure(scaffolderApi, {
            templateRef,
            project,
            branch,
            fields,
            ...(production ? { confirmation: typed } : {}),
          }),
        );
      } catch (error: unknown) {
        setFailure(error instanceof Error ? error.message : String(error));
        setPending(false);
      }
    },
  };
}

/** One line of the changes: a field, before and after. */
export interface ConfigurationPanelChange<F extends string, V> {
  field: F;
  before: V;
  after: V;
  derived: boolean;
}

/**
 * The panel of the « Configuration » tab, the same for the Odoo tab and the
 * generic one: the branch and the caller's tier, the live quota, « Comparer
 * avec », the changes and the refusals no row shows, the peak and the
 * cluster's headroom, the commit's note, the prod confirmation, « Annuler »
 * and « Appliquer ». `restart`: what the redeployment does, said in the
 * note (Odoo's); without it, the generic note.
 */
export function ConfigurationSidePanel<F extends string, V>(props: {
  branch: string;
  answer: Pick<
    ConfigurationResponse,
    | 'environment'
    | 'kind'
    | 'active'
    | 'access'
    | 'editable'
    | 'appenvPath'
    | 'quota'
  >;
  compare: Omit<ConfigurationReferenceState<FieldValues>, 'reference'> & {
    /** Whether the reference's configuration is read. */
    read: boolean;
    /** The fields differing from it. */
    differences: number;
  };
  /** The segmented control of the tab's rows (`useConfigurationRows`). */
  choice: <K extends string>(options: ConfigurationChoice<K>) => ReactNode;
  changes: ReadonlyArray<ConfigurationPanelChange<F, V>>;
  fieldLabel: (field: F) => string;
  valueText: (field: F, value: V) => string;
  /** The refusals no row of the tab shows. */
  problems: readonly string[];
  /** What the environment reserves at its peak, now and once applied. */
  peak: { before: Reservation; after: Reservation };
  restart?: string;
  apply: ConfigurationApply;
  canApply: boolean;
  onApply: () => void;
  /** Back to the current configuration (the typed confirmation cleared). */
  onCancel: () => void;
}) {
  const { branch, answer, compare, changes, apply, peak } = props;
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const { editable } = answer;
  const grant = answer.access === 'admin' ? 'admin' : 'editor';
  const margin = useClusterMargin(editable && answer.access === 'admin');
  const sleeping = answer.kind === 'permanent' && !answer.active;
  const file = `${answer.environment}/values.yaml`;

  let tier: string = t('configuration.tierRead');
  if (editable) {
    tier =
      grant === 'admin'
        ? t('configuration.tierAdmins')
        : t('configuration.tierEditors');
  }
  const { before, after } = peak;
  const deltaParts = [
    after.cpu !== before.cpu ? signed(after.cpu - before.cpu, formatCpu) : '',
    after.memory !== before.memory
      ? signed(after.memory - before.memory, formatMemory)
      : '',
  ].filter(Boolean);
  const impact = [
    t('configuration.peak', {
      cpu: formatCpu(after.cpu),
      memory: formatMemory(after.memory),
    }),
    deltaParts.length > 0 ? ` (${deltaParts.join(' · ')})` : '',
    margin
      ? ` — ${t('configuration.margin', {
          cpu: formatCpu(margin.cpu),
          memory: formatMemory(margin.memory),
        })}`
      : '',
  ].join('');
  let note: string;
  if (sleeping) {
    note = t('configuration.noteSleeping', {
      repo: answer.appenvPath,
      file,
      branch,
    });
  } else if (props.restart !== undefined) {
    note = t('configuration.note', {
      repo: answer.appenvPath,
      file,
      branch,
      restart: props.restart,
    });
  } else {
    note = t('configuration.noteGeneric', {
      repo: answer.appenvPath,
      file,
      branch,
    });
  }

  return (
    <SidePanel
      label={t('configuration.panelLabel')}
      className="waadoo-config__panel"
      head={
        <div className="waadoo-config__panel-head">
          <Text as="h3" variant="body-large" weight="bold">
            <EnvironmentTag branch={branch} asleep={sleeping} />
          </Text>
          <span className="waadoo-config__badge">{tier}</span>
        </div>
      }
    >
      <SidePanelSection title={t('configuration.quotaTitle')}>
        <QuotaGauges quota={answer.quota} sleeping={sleeping} branch={branch} />
      </SidePanelSection>

      {compare.references.length > 0 && (
        <SidePanelSection title={t('configuration.compareWith')}>
          {props.choice({
            label: t('configuration.compareWith'),
            selected: compare.referenceEnvironment ?? NO_REFERENCE,
            items: [
              ...compare.references.map(option => ({
                id: option.environment,
                label: option.label,
              })),
              { id: NO_REFERENCE, label: t('configuration.compareNone') },
            ],
            // Reading aid, not an edit: open to read-only callers too.
            disabled: false,
            onSelect: compare.select,
          })}
          {compare.read && compare.referenceLabel && (
            <Text as="p" variant="body-small" color="secondary">
              {compare.differences > 0
                ? t('configuration.differences', {
                    count: compare.differences,
                    branch: compare.referenceLabel,
                  })
                : t('configuration.noDifference', {
                    branch: compare.referenceLabel,
                  })}
            </Text>
          )}
        </SidePanelSection>
      )}

      <SidePanelSection
        title={t('configuration.changesTitle', {
          changes: String(changes.length),
        })}
      >
        {changes.length === 0 ? (
          <Text as="p" variant="body-small" color="secondary">
            {t('configuration.noChanges')}
          </Text>
        ) : (
          <ul className="waadoo-config__changes">
            {changes.map(change => (
              <li key={change.field}>
                <span className="waadoo-config__change-name">
                  {props.fieldLabel(change.field)}
                  {change.derived && (
                    <span className="waadoo-config__derived">
                      {' '}
                      ({t('configuration.derived')})
                    </span>
                  )}
                </span>
                <span className="waadoo-config__change-values">
                  <s>{props.valueText(change.field, change.before)}</s>{' '}
                  <strong>{props.valueText(change.field, change.after)}</strong>
                </span>
              </li>
            ))}
          </ul>
        )}
        {props.problems.map((text, index) => (
          <Text
            as="p"
            variant="body-small"
            color="danger"
            key={`${index}-${text}`}
          >
            {text}
          </Text>
        ))}
        {sleeping && changes.length > 0 && (
          <Text as="p" variant="body-small" color="secondary">
            {t('configuration.appliedAtWake', { count: changes.length })}
          </Text>
        )}
        <Text
          as="p"
          variant="body-small"
          color="secondary"
          className="waadoo-config__impact"
        >
          {impact}
        </Text>
        {editable && (
          <Text
            as="p"
            variant="body-small"
            color="secondary"
            className="waadoo-config__note"
          >
            {note}
          </Text>
        )}
        {editable && apply.production && changes.length > 0 && (
          <TextField
            label={t('envPower.confirmLabel', {
              confirmation: apply.expected,
            })}
            value={apply.typed}
            onChange={apply.setTyped}
            isDisabled={apply.pending}
          />
        )}
        {apply.failure && (
          <Text as="p" variant="body-small" color="danger" role="alert">
            {t('envPower.launchFailed', { message: apply.failure })}
          </Text>
        )}
        {editable && (
          <div className="waadoo-config__actions">
            <Button
              variant="secondary"
              isDisabled={apply.pending || changes.length === 0}
              onPress={() => {
                props.onCancel();
                apply.setTyped('');
              }}
            >
              {t('configuration.cancel')}
            </Button>
            <Button
              variant="primary"
              isDisabled={!props.canApply}
              isPending={apply.pending}
              onPress={props.onApply}
            >
              {t('configuration.apply')}
            </Button>
          </div>
        )}
      </SidePanelSection>
    </SidePanel>
  );
}
